import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Flag, Inbox, Pause, Pencil, Play, Trash2 } from 'lucide-react'
import { api, type OutreachDetail, type OutreachGroup, type OutreachStatus } from '@/api/client'
import {
  fmtCount,
  sentOf,
  sumCounts,
  useCreatorMutation,
  useOutreach,
  useOutreachRecipients,
  windowText,
} from '@/lib/creators'
import {
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  PageHeader,
  PlatformMark,
  Segmented,
  Select,
  Skeleton,
  StatusPill,
  When,
  errorText,
  useQueryParam,
  useToast,
  useWidePage,
} from '@/ui'
import { Section } from '../campaigns/shared'
import { GroupDrawer } from './GroupDrawer'
import { ApprovalCard, QueueCard, SentCard, type CardCtx } from './RecipientCards'
import { ResultsTable } from './ResultsTable'

type Tab = 'ready' | 'approved' | 'sent' | 'results'

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-meta text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate text-body font-medium tabular-nums">{children}</div>
    </div>
  )
}

function Cards({
  id,
  tab,
  groupId,
  detail,
  ctx,
}: {
  id: number
  tab: Exclude<Tab, 'results'>
  groupId: number
  detail: OutreachDetail
  ctx: CardCtx
}) {
  const q = useOutreachRecipients(id, tab, groupId, !!detail.generating?.running)
  const items = q.data?.items
  const platform = detail.outreach.platform
  if (q.error && !q.data) return <ErrorState title="사람을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} />
  if (!items) return <Skeleton className="h-40" />
  if (items.length === 0) {
    const text = {
      ready: ['승인 기다리는 메시지가 없다', detail.generating?.running ? '초안을 쓰는 중이다.' : '초안을 쓰면 여기에 쌓인다.'],
      approved: ['보낼 차례인 메시지가 없다', '승인한 메시지가 여기로 온다.'],
      sent: ['아직 보낸 것이 없다', undefined],
    }[tab]
    return <EmptyState icon={Inbox} title={text[0]!} description={text[1]} />
  }
  return (
    <ul aria-label={tab === 'ready' ? '승인 대기 목록' : tab === 'approved' ? '보낼 차례 목록' : '보낸 목록'} className="grid items-start gap-3 lg:grid-cols-2">
      {items.map((r) =>
        tab === 'ready' ? (
          <ApprovalCard key={`${r.id}:${r.message?.body ?? ''}`} r={r} ctx={ctx} platform={platform} />
        ) : tab === 'approved' ? (
          <QueueCard key={r.id} r={r} ctx={ctx} />
        ) : (
          <SentCard key={r.id} r={r} ctx={ctx} />
        ),
      )}
    </ul>
  )
}

/** /creators/outreaches/:id: one outreach — approve, send, follow up, compare. */
export default function OutreachPage() {
  useWidePage()
  const id = Number(useParams().id)
  const q = useOutreach(id)
  const toast = useToast()
  const navigate = useNavigate()
  const [tab, setTab] = useQueryParam('tab', 'ready')
  const [group, setGroup] = useQueryParam('group')
  const [editing, setEditing] = useState<OutreachGroup | null>(null)
  const [ending, setEnding] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [genError, setGenError] = useState<string | null>(null)
  const detail = q.data

  const setStatus = useCreatorMutation((status: OutreachStatus) => api.updateOutreach(id, { status }))
  const generate = useCreatorMutation(() => api.generateOutreach(id))
  const del = useCreatorMutation(() => api.deleteOutreach(id))
  const parent = { to: '/creators/outreaches', label: 'DM 보내기' }

  if (!detail) {
    return (
      <>
        <PageHeader title="발송" parent={parent} />
        {q.error ? <ErrorState title="발송을 불러오지 못했다" error={q.error} onRetry={() => q.refetch()} /> : <Skeleton className="h-48" />}
      </>
    )
  }
  const { outreach: o, groups } = detail
  const counts = sumCounts(groups.map((g) => g.counts))
  const gen = detail.generating
  const view: Tab = (['ready', 'approved', 'sent', 'results'] as const).includes(tab as Tab) ? (tab as Tab) : 'ready'
  const groupId = Number(group) > 0 && groups.some((g) => g.id === Number(group)) ? Number(group) : 0
  const ctx: CardCtx = { language: o.language, groups, nextSendAt: detail.next_send_at }
  const sentTotal = sentOf(counts)

  const change = async (status: OutreachStatus, done: string) => {
    try {
      await setStatus.mutateAsync(status)
      toast.success(done)
    } catch (e) {
      toast.error(`바꾸지 못했다: ${errorText(e)}`)
    }
  }

  const startDrafts = async () => {
    setGenError(null)
    try {
      const r = await generate.mutateAsync()
      toast.success(`${r.started}명의 초안을 쓴다`)
    } catch (e) {
      setGenError(errorText(e))
    }
  }

  const actions = (
    <>
      {(o.status === 'draft' || o.status === 'paused') && (
        <Button variant="primary" icon={Play} loading={setStatus.isPending} onClick={() => void change('active', '시작했다')}>
          시작
        </Button>
      )}
      {o.status === 'active' && (
        <Button icon={Pause} loading={setStatus.isPending} onClick={() => void change('paused', '멈췄다')}>
          멈춤
        </Button>
      )}
      {o.status !== 'done' && (
        <Button icon={Flag} onClick={() => setEnding(true)}>
          끝내기
        </Button>
      )}
      {sentTotal === 0 && (
        <Button variant="dangerOutline" icon={Trash2} onClick={() => setDeleting(true)}>
          삭제
        </Button>
      )}
    </>
  )

  const tabs = [
    { value: 'ready', label: '승인 대기', count: counts.ready },
    { value: 'approved', label: '보낼 차례', count: counts.approved },
    { value: 'sent', label: '보낸 것', count: sentTotal },
    { value: 'results', label: '결과' },
  ] as const

  return (
    <>
      <PageHeader
        parent={parent}
        docTitle={o.name}
        title={
          <span className="inline-flex flex-wrap items-center gap-2">
            <PlatformMark platform={o.platform} />
            {o.name}
            <StatusPill domain="outreach" status={o.status} className="align-middle" />
          </span>
        }
        description={o.product_name ? `${o.product_name} · 그룹 ${groups.length}개 · ${fmtCount(counts.found + counts.ready + counts.approved + sentTotal + counts.skipped + counts.do_not_contact)}명` : undefined}
        actions={actions}
      >
        <div className="grid grid-cols-2 gap-x-4 gap-y-3 rounded-lg border border-border bg-card p-4 sm:grid-cols-4">
          <Stat label="오늘 보냄">
            {detail.sent_today} / {o.daily_limit}
          </Stat>
          <Stat label="다음 발송">{o.status === 'active' ? <When iso={detail.next_send_at} mode="slot" /> : '—'}</Stat>
          <Stat label="보내는 시간">
            {windowText(detail.window.open)}–{windowText(detail.window.close)}
            <span className="ml-1 text-meta font-normal text-muted-foreground">{detail.window.open_now ? '지금 열려 있다' : '지금은 닫혀 있다'}</span>
          </Stat>
          <Stat label="시간대">{o.timezone}</Stat>
        </div>
      </PageHeader>

      {gen?.running && (
        <div role="status" aria-label="초안 진행" className="mb-4 space-y-1.5 rounded-lg border border-border bg-card p-4">
          <div className="flex items-center justify-between text-table">
            <span>초안을 쓰는 중</span>
            <span className="tabular-nums">
              {gen.done} / {gen.total}
            </span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <div className="h-full bg-status-publishing transition-all" style={{ width: `${gen.total > 0 ? Math.min(100, (gen.done / gen.total) * 100) : 0}%` }} />
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Segmented label="발송 화면" value={view} onChange={setTab} options={tabs.map((t) => ({ ...t }))} />
          {view !== 'results' && (
            <div className="flex items-center gap-2">
              {groups.length > 1 && (
                <Select aria-label="그룹 보기" value={groupId || ''} onChange={(e) => setGroup(e.target.value)} className="h-8 w-auto text-table">
                  <option value="">그룹 전체</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.label}
                    </option>
                  ))}
                </Select>
              )}
              {(groupId > 0 || groups.length === 1) && (
                <Button size="sm" icon={Pencil} onClick={() => setEditing(groups.find((g) => g.id === groupId) ?? groups[0])}>
                  그룹 설정
                </Button>
              )}
            </div>
          )}
        </div>

        {view !== 'results' && counts.found > 0 && !gen?.running && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-muted/60 p-3 text-table">
            <span>초안이 아직 없는 사람이 {fmtCount(counts.found)}명 있다.</span>
            <Button size="sm" variant="primary" loading={generate.isPending} onClick={() => void startDrafts()}>
              초안 쓰기
            </Button>
          </div>
        )}
        {genError && (
          <p role="alert" className="break-words text-table text-status-failed">
            {genError}
          </p>
        )}

        {view === 'results' ? (
          <Section title="그룹별 결과">
            <ResultsTable groups={groups} onSettings={setEditing} />
          </Section>
        ) : (
          <Cards id={id} tab={view} groupId={groupId} detail={detail} ctx={ctx} />
        )}
      </div>

      {editing && (
        <GroupDrawer
          key={editing.id}
          outreachId={id}
          group={groups.find((g) => g.id === editing.id) ?? editing}
          language={o.language}
          platform={o.platform}
          onClose={() => setEditing(null)}
        />
      )}
      <ConfirmDialog
        open={ending}
        onOpenChange={setEnding}
        title="발송을 끝낼까"
        description="끝내면 더 보내지 않는다. 보낸 것과 결과는 그대로 남는다."
        confirmLabel="끝내기"
        confirmVariant="destructive"
        loading={setStatus.isPending}
        onConfirm={async () => {
          await change('done', '끝냈다')
          setEnding(false)
        }}
      />
      <ConfirmDialog
        open={deleting}
        onOpenChange={setDeleting}
        title="발송을 지울까"
        description="아직 아무것도 보내지 않은 발송만 지울 수 있다. 쓴 초안도 함께 지워진다."
        confirmLabel="지우기"
        confirmVariant="destructive"
        loading={del.isPending}
        onConfirm={async () => {
          try {
            await del.mutateAsync()
            toast.success('발송을 지웠다')
            navigate('/creators/outreaches')
          } catch (e) {
            toast.error(`지우지 못했다: ${errorText(e)}`)
            setDeleting(false)
          }
        }}
      />
    </>
  )
}
